import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { Materialize } from '@adornis/materialized-views/decorators/materialize.js';
import { validate } from '@adornis/validation/decorators.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { minLength } from '@adornis/validation/functions/length.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import type { InsertOneResult, UpdateResult } from 'mongodb';
import { BillingPosition } from '../../billingPositions/entities/BillingPosition.js';
import { Booking } from '../../bookings/entities/Booking.js';
import { repopulateCache } from '../../entity-cache/entity-cache.js';
import { MicrosoftGroupData } from '../../microsoft-graph/entities/MicrosoftGroupData.js';
import { removeGroupInEntra } from '../../microsoft-graph/server/groups/microsoft-remove-group.js';
import { enqueueMicrosoftGroupSync } from '../../microsoft-graph/server/microsoft-sync-queue.js';
import { type BillingLockState, isBillingLocked } from '../api/groupBillingLock.js';
import { materializedGroupSelectionset } from './materializedGroupSelectionset.js';

@Materialize({
  selectionSet: materializedGroupSelectionset(),
})
@Entity()
export class Group extends MongoEntity {
  public static override _class = 'Group';
  public static override _collectionName = 'groups';

  override toString() {
    return this.name;
  }

  @Field(() => String)
  @validate(minLength(1))
  name: Maybe<string>;

  @Field(() => String)
  kuerzel: Maybe<string>;

  @Field(() => [String], { default: v => v ?? [] })
  @validate(nonOptional())
  parentIDs: Maybe<string[]>;

  @DataLoadedField(() => [Group], ({ parentIDs }) => ({ _id: { $in: parentIDs ?? [] } }))
  parents: Maybe<Group[]>;

  /**
   * What the bookings of this group pay. One position per group: a booking that owes something
   * else is a booking on the group that bills it. A booking may override the price, nothing else
   * (`Booking.billingPosition`); the rhythm is the group's and fixed once it billed
   * ({@link billingRhythmFixed}).
   */
  @Field(() => BillingPosition)
  billingPosition: Maybe<BillingPosition>;

  /**
   * Whether the rhythm of the Abrechnungsposition can no longer change - see
   * {@link isBillingRhythmFixed}. Changes only with a lock export, so nothing is stored.
   */
  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(() => Boolean, {
    resolve: function (this: Group) {
      return async () => !!this._id && (await isBillingRhythmFixed(this._id));
    },
  })
  billingRhythmFixed: Maybe<boolean>;

  @Field(() => MicrosoftGroupData, { default: v => v ?? new MicrosoftGroupData({}) })
  microsoftGroupData: Maybe<MicrosoftGroupData>;

  @Field(() => String)
  iban: Maybe<string>;

  /**
   * The lock a SEPA lock export leaves behind: its Stichtag and the first day a booking of the
   * group is due again. Locked is derived from the two and today (`groups/api/groupBillingLock.ts`);
   * a lock without a release day holds until "Freigeben". Both are calendar days.
   */
  @Field(() => Date)
  billingLockStichtag: Maybe<Date>;

  @Field(() => Date)
  billingLockReleaseDay: Maybe<Date>;

  /**
   * The boundary for {@link isBillingRhythmFixed}: the group form greys the rhythm out, but
   * `updateEntity` takes any write. Removing the position counts as a change too, or removing and
   * adding it again would switch the rhythm after all.
   */
  static override async beforeUpdate(oldEntity: PartialEntityData<Group>, update: Group): Promise<void> {
    assertNotInBilling(
      oldEntity,
      (update.has('billingPosition') && changesBillingPosition(oldEntity.billingPosition, update.billingPosition)) ||
        (update.has('parentIDs') &&
          [...(oldEntity.parentIDs ?? [])].sort().join() !== [...(update.parentIDs ?? [])].sort().join()) ||
        (update.has('iban') && (oldEntity.iban ?? null) !== (update.iban ?? null)),
    );
    if (
      update.has('billingPosition') &&
      (oldEntity.billingPosition?.rhythm ?? null) !== (update.billingPosition?.rhythm ?? null) &&
      oldEntity._id &&
      (await isBillingRhythmFixed(oldEntity._id))
    ) {
      throw new ExpectedError(
        'Der Rhythmus der Abrechnungsposition kann nach der ersten Abrechnung nicht mehr geändert werden - auch nicht von Administratoren. Für einen anderen Rhythmus bitte eine neue Gruppe anlegen.',
      );
    }

    return MongoEntity.beforeUpdate(oldEntity, update);
  }

  static override async afterUpdate(
    oldEntity: EntityData<Group>,
    newEntity: Group,
    result: UpdateResult,
  ): Promise<void> {
    // schedule a sync so microsoft can keep track of the group. Deliberately not executed here: the
    // group repair is the single place that decides what a group has to look like in Entra, and saving
    // a group must not fail or hang because the Graph API does.
    await enqueueMicrosoftGroupSync(newEntity._id);

    if (newEntity._class === 'School') {
      await repopulateCache('schools');
    }

    return MongoEntity.afterUpdate(oldEntity, newEntity, result);
  }

  static override async afterCreate(entity: Group, result: InsertOneResult): Promise<void> {
    // make request to microsoft to create the group
    if (entity._class === 'School') {
      await repopulateCache('schools');
    }
    return MongoEntity.afterCreate(entity, result);
  }

  /**
   * A group that a billing position references as Kostenstelle must not disappear while money is
   * still booked onto it: the SEPA export resolves `billingPosition.kostenstelleID` at run time
   * and a dangling reference fails the whole run (Kostenstellenfehler, 2026-08). Only bookings
   * that are not ended block - past bookings never reach an export again, so a Kostenstelle
   * referenced solely by history may go.
   */
  static override async beforeRemove(id: string, entityData: EntityData<Group>): Promise<void> {
    const bookingCol = await getRawCollection<EntityData<Booking>>(Booking._collectionName);
    const groupCol = await getRawCollection<EntityData<Group>>(Group._collectionName);

    const referencingGroups = await groupCol
      .find({ 'billingPosition.kostenstelleID': id }, { projection: { _id: 1, name: 1 } })
      .toArray();
    // not ended = still running or not yet started; both will be exported again
    const bookingsOnReferencingGroups = referencingGroups.length
      ? await bookingCol.countDocuments({
          groupIDs: { $in: referencingGroups.map(group => group._id) },
          $or: [{ endDate: null }, { endDate: { $exists: false } }, { endDate: { $gte: todayAsCalendarDay() } }],
        })
      : 0;

    if (bookingsOnReferencingGroups) {
      throw new ExpectedError(
        `Die Gruppe kann nicht gelöscht werden: Sie ist als Kostenstelle der Abrechnungspositionen der Gruppen ${referencingGroups.map(group => `"${group.name ?? group._id}"`).join(', ')} mit ${bookingsOnReferencingGroups} laufenden Buchungen hinterlegt. Bitte zuerst die Buchungen beenden oder die Abrechnungspositionen auf eine andere Kostenstelle umstellen.`,
      );
    }

    return MongoEntity.beforeRemove(id, entityData);
  }

  static override async afterRemove(id: string, entityData: EntityData<Group>): Promise<void> {
    // delete group in entra
    if (baseConfig.get('DEPLOYMENT') !== 'testing')
      await removeGroupInEntra(entityData.microsoftGroupData?.microsoftID);

    if (entityData._class === 'School') {
      await repopulateCache('schools');
    }

    // remove all bookings
    const bookingCol = await getRawCollection<EntityData<Booking>>(Booking._collectionName);
    // TODO update permissions from all users of the group

    return MongoEntity.afterRemove(id, entityData);
  }
}

/**
 * A group's rhythm is fixed for good once the group is in billing: while it is locked, and once a
 * lock export collected anything from it - "Freigeben" clears the lock, the `billedOn` markers stay.
 * `billedOn` means "last collection" for monthly and yearly but "done for good" for once, so a
 * switch after a collection silently stops or repeats it (ZVGPVQ). Nobody may change it then, not
 * even an admin: a different rhythm is a new group.
 */
export async function isBillingRhythmFixed(groupID: string): Promise<boolean> {
  // loaded lazily: SepaMandat's imports reach School, which extends this class
  const { SepaMandat } = await import('../../sepa/entities/SepaMandat.js');
  const groupCol = await getRawCollection<EntityData<Group>>(Group._collectionName);
  const bookingCol = await getRawCollection<EntityData<Booking>>(Booking._collectionName);
  const mandateCol = await getRawCollection<EntityData<InstanceType<typeof SepaMandat>>>(SepaMandat._collectionName);
  const projection = { projection: { _id: 1 } };

  return (
    !!(await groupCol.findOne({ _id: groupID, billingLockStichtag: { $ne: null } }, projection)) ||
    !!(await bookingCol.findOne({ groupIDs: groupID, billedOn: { $ne: null } }, projection)) ||
    !!(await mandateCol.findOne({ groupID, billedOn: { $ne: null } }, projection))
  );
}

/** What the export books from an Abrechnungsposition - everything but its id and template. */
const BILLING_POSITION_FIELDS = [
  'name',
  'kuerzel',
  'kostenstelleID',
  'priceInCents',
  'rhythm',
  'buchungskonto',
] as const;

function changesBillingPosition(
  stored: Maybe<Partial<BillingPosition>>,
  next: Maybe<Partial<BillingPosition>>,
): boolean {
  if (!stored || !next) return !stored !== !next;
  return BILLING_POSITION_FIELDS.some(field => (stored[field] ?? null) !== (next[field] ?? null));
}

/**
 * What the export reads from a group besides its name - the Abrechnungsposition, the parents that place it in a
 * school, the IBAN, the Kostenstelle number parts - stays as the lock export saw it while the group is in billing.
 * For everybody, admins included, and for a school too: "Freigeben" first. The general information stays editable.
 */
export function assertNotInBilling(stored: BillingLockState, changesBilling: boolean): void {
  if (!changesBilling || !isBillingLocked(stored, todayAsCalendarDay())) return;

  const release = stored.billingLockReleaseDay
    ? `frei ab ${formatCalendarDay(stored.billingLockReleaseDay, 'dd.MM.yyyy')}`
    : 'bis zur Freigabe';
  throw new ExpectedError(
    `Die Gruppe ist in der Abrechnung (${release}). Abrechnungsposition, übergeordnete Gruppen, IBAN und Kostenstelle können erst nach der Freigabe geändert werden.`,
  );
}
